/**
 * Central dispatcher for server-driven UI intents.
 *
 * One extension point: domains call `registerIntent(kind, handler)`. The
 * dispatcher owns every safety/session/dedupe guard so they live in one place.
 * Fail-closed throughout: only a well-formed, session-matched, 'auto' intent
 * for a registered kind ever executes.
 */
import type { UiIntentEvent } from '../chat/websocketTypes';

/** Dependencies injected at call-site so the dispatcher stays framework-agnostic. */
export interface IntentContext {
  /** React Router navigate or equivalent. */
  navigate: (path: string) => void;
  /** Accessible live-region announcement for screen readers / toast. */
  announce: (message: string) => void;
  /** Enqueue a confirm-gated intent for user approval before execution. */
  enqueueConfirm: (event: UiIntentEvent) => void;
  /** Zustand store getState() snapshot; typed loosely to avoid a cycle. */
  store: unknown;
  /** The client's current WS session id; null when no session is active. */
  sessionId: string | null;
  /** The conversation currently open in the UI; null when none. Used by handlers for conversation-scoped decisions. */
  activeConversationId: string | null;
  /**
   * The workspace surface (pane/tab) this dispatch belongs to. Handlers that
   * write surface-keyed state must use it, or they land on the wrong pane.
   */
  surfaceId: string;
}

/** Handler registered for a given intent kind. */
export type IntentHandler = (
  args: Record<string, unknown>,
  ctx: IntentContext,
) => void;

const registry = new Map<string, IntentHandler>();

/**
 * Bounded set of ids that have already EXECUTED (not merely been enqueued).
 * Writing seenIds at execution time (not dispatch time) ensures a confirm-gate
 * can still run after being enqueued.
 */
const seenIds = new Set<string>();
const SEEN_LIMIT = 50;

/** Add id to the seen set and evict the oldest entry when the limit is reached. */
function markSeen(id: string): void {
  seenIds.add(id);
  if (seenIds.size >= SEEN_LIMIT) {
    const oldest = seenIds.values().next().value as string | undefined;
    if (oldest !== undefined) seenIds.delete(oldest);
  }
}

/** Structural guard: ensure the event has the minimum required fields. */
function isWellFormedIntent(e: UiIntentEvent): boolean {
  return (
    typeof e.id === 'string' &&
    e.id.length > 0 &&
    typeof e.kind === 'string' &&
    typeof e.safety === 'string'
  );
}

/** Register a handler for an intent kind. Throws on a duplicate kind (collision = bug). */
export function registerIntent(kind: string, handler: IntentHandler): void {
  if (registry.has(kind)) {
    throw new Error(`Duplicate ui_intent kind: ${kind}`);
  }
  registry.set(kind, handler);
}

/**
 * Shared execution path for the auto branch and a confirm-chip click, so the
 * same idempotency and announce guarantees hold either way.
 *
 * Callers must have already checked session scope and handler existence before
 * calling this.
 */
export function runIntent(
  event: UiIntentEvent,
  handler: IntentHandler,
  ctx: IntentContext,
): void {
  if (seenIds.has(event.id)) return; // already executed (e.g. double click on confirm)
  markSeen(event.id);
  handler(event.args, ctx);
  if (event.reason) ctx.announce(event.reason);
}

/**
 * Entry point called by the WS handler for every `ui_intent` event.
 *
 * Guard order (fail-closed):
 *   1. Well-formed — must have non-empty id, kind string, and safety string.
 *   2. Session scope — event.session_id must equal ctx.sessionId (null never matches).
 *   3. Idempotency — already-executed ids are silently dropped.
 *   4. Handler existence — unknown kinds are ignored for forward-compat.
 *   5. Safety — 'auto' executes; 'confirm' enqueues; anything else is dropped.
 */
export function dispatchUiIntent(event: UiIntentEvent, ctx: IntentContext): void {
  if (!isWellFormedIntent(event)) return;
  if (event.session_id !== ctx.sessionId) return;
  if (seenIds.has(event.id)) return;
  const handler = registry.get(event.kind);
  if (!handler) return; // unknown kind → forward-compat ignore
  if (event.safety === 'auto') {
    runIntent(event, handler, ctx);
  } else if (event.safety === 'confirm') {
    ctx.enqueueConfirm(event); // idempotent by id in the confirm queue
  }
  // any other safety value: fail-closed, drop silently
}

/** Test-only: clear registry and dedupe state between tests. */
export function __resetDispatcherForTests(): void {
  registry.clear();
  seenIds.clear();
}
